// Investor Outreach subscribe / unsubscribe page (ATAI-271), at /accountnew/manageinvestoroutreach/:type.
// Same layout as Connect's page. Figma: subscribe 1412:153698, unsubscribe 1255:125220.
// Calls the real S2/S3 endpoints (investorOutreachApi.js); subscribe makes a real card charge.
// Cancelling never asks about Connect: it is always kept.
import classNames from 'classnames';
import * as moment from 'moment';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect, withRouter } from 'react-router-dom';
import { ReactSVG } from 'react-svg';
import LoadablePanel from 'components/hoc/loadable/Panel';
import currencyFormat from 'utils/currency/numberToPrice';
import downFullArrow from '../../../assets/images/account-page/down-full-arrow.svg';
import investorOutreachLogo from '../../../assets/images/investor-outreach-logo.svg';
import { selectCardList, selectUserId } from '../../../data/user';
import CreditCardDropdown from '../components/CreditCardDropdown';
import AccountLogo from '../components/AccountLogo';
import CVV99Modal from '../components/cvv99Modal';
import css from '../style.scss';
import { useToast } from '../../components/ToastMessage/ToastManager';
import { getPricingPreview, getCancelPreview, subscribe, unsubscribe } from './investorOutreachApi';
import { refreshSubscriptionCards } from '../refreshSubscriptionCards';

// Messages for the backend's error codes. IO_CVV_REQUIRED is handled in confirmChanges instead.
const ERROR_MESSAGES = {
  IO_PRICE_CHANGED:
    'The price changed since you last viewed this page. Please review the updated amount and try again.',
  IO_PAYMENT_DECLINED:
    'Your card was declined. Please try a different payment method — you have not been charged.',
  IO_ACCOUNT_NOT_ACTIVE:
    'There’s a billing issue on this account. Please contact support to resolve it before subscribing.',
  IO_CONNECT_UNAVAILABLE:
    'Investor Outreach couldn’t be added because PropStream Connect isn’t available on this account. Please contact support.',
  IO_NON_BILLABLE_ACCOUNT: 'This account can’t make purchases. Please contact support.',
  IO_ANNUAL_TERM_UNKNOWN:
    'We couldn’t determine your renewal date. Please contact support to complete this purchase.',
  IO_ALREADY_SUBSCRIBED: 'This account already has an active Investor Outreach subscription.',
  IO_NOT_SUBSCRIBED: 'There’s no active Investor Outreach subscription to cancel.',
  IO_NOT_PENDING_CANCELLATION:
    'This subscription isn’t pending cancellation, so there’s nothing to restore.',
  IO_TRIAL_ACCOUNT:
    'Investor Outreach isn’t available while your account is on a trial. Please activate your subscription first.',
  IO_NOT_AVAILABLE_ON_PLAN: 'Investor Outreach isn’t available on your current plan.',
  IO_NOT_PRIMARY_ACCOUNT_HOLDER: 'Only the primary account holder can manage Investor Outreach.',
};
const FALLBACK_ERROR_MESSAGE = 'Something went wrong, please try again later';
const errorMessageFor = (result) => ERROR_MESSAGES[result?.errorCode] || FALLBACK_ERROR_MESSAGE;

const ManageInvestorOutreachScreen = (props) => {
  const { type } = props.match.params; // 'subscribe' | 'unsubscribe'
  const isUnsubscribe = type === 'unsubscribe';

  const dispatch = useDispatch();
  const userId = useSelector(selectUserId);
  const cardList = useSelector(selectCardList);
  const [selectedCard, setSelectedCard] = useState(cardList[0] || {});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showCVVModal, setShowCVVModal] = useState(false);
  const showToast = useToast();
  const isValidType = type === 'subscribe' || isUnsubscribe;
  // Bumped to refetch the preview, e.g. after IO_PRICE_CHANGED.
  const [reloadKey, setReloadKey] = useState(0);

  // Preview data: S3 pricing (subscribe) or S2 cancel preview (unsubscribe).
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  useEffect(() => {
    if (!userId || !isValidType) {
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    (isUnsubscribe ? getCancelPreview(userId) : getPricingPreview(userId)).then((result) => {
      if (cancelled) {
        return;
      }
      if (result.ok) {
        setPreview(result.data);
      } else {
        setLoadError(errorMessageFor(result));
      }
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [userId, isUnsubscribe, isValidType, reloadKey]);

  const card = selectedCard?.paymentMethodId ? selectedCard : cardList[0] || {};
  const handleCardChange = (selectedOption) => {
    setSelectedCard(selectedOption);
  };

  // The button reads "Back to Tiers" per Figma (as on Connect). There is no tiers page here, so it goes to Account.
  const goBack = () => {
    props.history.push('/accountnew/landing');
  };

  // Called first without a cardCode (opens the CVV modal if needed), then again from the modal. Cancel needs no CVV.
  const confirmChanges = async ({ cardCode = '' } = {}) => {
    if (!preview) {
      return;
    }
    // Like Connect's page: ask for the CVV whenever something is charged today.
    if (!isUnsubscribe && Number(preview.dueToday) > 0 && !cardCode) {
      setShowCVVModal(true);
      return;
    }

    setIsSubmitting(true);
    const result = isUnsubscribe
      ? await unsubscribe({
          userId,
          alsoCancelConnect: false,
        })
      : await subscribe({
          userId,
          purchasePaymentMethodId: card.paymentMethodId,
          cardCode: cardCode || undefined,
          // The server rejects with IO_PRICE_CHANGED if the price moved since the preview.
          shownDueToday: preview.dueToday,
        });
    setIsSubmitting(false);

    if (!result.ok) {
      if (result.errorCode === 'IO_CVV_REQUIRED') {
        // The server needs a CVV: ask again. Nothing was charged.
        setShowCVVModal(true);
        return;
      }
      setShowCVVModal(false);
      showToast(errorMessageFor(result), 'error');
      if (result.errorCode === 'IO_PRICE_CHANGED') {
        // Show the new amount before the next attempt.
        setReloadKey((k) => k + 1);
      }
      return;
    }

    setShowCVVModal(false);
    showToast(
      isUnsubscribe
        ? 'Unsubscribed from Investor Outreach successfully'
        : 'Investor Outreach subscribed successfully',
      'success'
    );
    // Refresh the plan payload so the Account page and nav reflect the change without a reload, like
    // Connect's page does. The Account card re-reads its own state when it mounts again.
    // Force-refresh the plan, Connect and the cached Investor Outreach entitlement so the Account cards,
    // the search route and the upsell reflect the change without a reload (buying adds or switches
    // Connect; cancelling with "cancel both" ends it).
    refreshSubscriptionCards(dispatch, userId);
    goBack();
  };

  // connectBefore (from S3) drives the from → to card.
  const heldConnect = preview?.connectBefore?.hasConnect || preview?.connectBefore?.includedInPlan;
  const connectAdded = Boolean(preview?.connectAdded);
  const fromLabel = isUnsubscribe
    ? 'Investor Outreach + Propstream Connect'
    : heldConnect
      ? 'Propstream Connect'
      : 'None';
  // Figma: the result is always the Investor Outreach + Connect bundle.
  const toLabel = isUnsubscribe ? 'Propstream Connect' : 'Investor Outreach + Propstream Connect';

  const previewReady = Boolean(preview) && !loading;

  // Only subscribe and unsubscribe are valid; never open the charge path for any other value.
  if (!isValidType) {
    return <Redirect to="/accountnew/landing" />;
  }

  return (
    <div className={css.clsInnerMainBody}>
      {/* The page frame stays up while the preview loads; only the preview-driven parts show a loader. */}
      <div>
        <div className="container">
          <div className={css.clsLAReviewPaySec}>
            <div className="row justify-content-center" style={{ paddingBottom: '13rem' }}>
              <div className="col-lg-4">
                <FromToAddOnCard
                  fromLabel={!isUnsubscribe && !preview ? '—' : fromLabel}
                  toLabel={toLabel}
                />
              </div>

              <div className="col-lg-5">
                {loadError && (
                  <div className={css.clsLAReviewPayCard2}>
                    <h3 className={css.clsLAReviewHeading}>
                      <AccountLogo />
                      Investor Outreach
                    </h3>
                    <div className={css.clsLAReviewContentPara}>
                      <p className={css.clsPara}>{loadError}</p>
                    </div>
                  </div>
                )}
                {!loadError && isUnsubscribe && (
                  <ReviewConfirmUnsubscribe preview={preview || {}} ready={previewReady} />
                )}
                {!loadError && !isUnsubscribe && (
                  <ReviewConfirmSubscribe
                    preview={preview || {}}
                    ready={previewReady}
                    connectAdded={connectAdded}
                    cardList={cardList}
                    onCardChange={handleCardChange}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className={css.clsInnerFooter}>
        <button className={classNames(css.clsBackBtn)} onClick={goBack} disabled={isSubmitting}>
          Back to Tiers
        </button>
        <button
          className={classNames(
            css.btn_xl,
            css.btn_primary,
            isUnsubscribe ? css.clsIOConfirmBtn : css.clsBtnOrng
          )}
          onClick={confirmChanges}
          disabled={isSubmitting || loading || Boolean(loadError)}
        >
          {isUnsubscribe ? 'Confirm Changes' : 'Subscribe Now'}
        </button>
      </div>

      {showCVVModal && (
        <CVV99Modal
          showCVVModal={showCVVModal}
          cardEnding={card?.cardEnding}
          cvvLength={card?.cardCVVLength}
          handleSubmitCVV={confirmChanges}
          handleCancel={() => setShowCVVModal(false)}
          handleClose={() => setShowCVVModal(false)}
        />
      )}
    </div>
  );
};

// Left card (Figma 1177:102323): what the account has now → what it will have.
export const FromToAddOnCard = ({ fromLabel, toLabel }) => (
  <div className={css.clsLAReviewPayCard}>
    <div className={css.clsLAReviewLeadAutoDetils}>
      {/* Figma 1177:102410: the logo fills the card width (296x111). */}
      <img
        src={investorOutreachLogo}
        alt="Investor Outreach"
        style={{ width: '100%', height: 'auto', display: 'block' }}
      />
    </div>
    <div className={css.clsLAReviewTier}>
      <h4 className={css.clsTxtLine1}>ADD-ON</h4>
      <h4 className={css.clsTxtLine2}>{fromLabel}</h4>
    </div>
    <div className="text-center">
      <ReactSVG src={downFullArrow} className={classNames(css.mb_16, css.mt_16)} />
    </div>
    <div className={css.clsLAReviewTier}>
      <h4 className={css.clsTxtLine1}>ADD-ON</h4>
      <h4 className={css.clsTxtLine2}>{toLabel}</h4>
    </div>
  </div>
);

// Confirm panel (Figma 1412:153698). Amounts are shown exactly as the S3 preview returns them.
const ReviewConfirmSubscribe = ({ preview, ready, connectAdded, cardList, onCardChange }) => {
  const frequencyLabel = preview.billingFrequency === 'year' ? 'year' : 'month';
  const firstBillingDate = moment(preview.firstBillingDate);
  return (
    <div className={css.clsLAReviewPayCard2}>
      <h3 className={css.clsLAReviewHeading}>
        <AccountLogo />
        Review And Confirm Changes
      </h3>
      {!ready && (
        // The hover loader fills its wrapper, so give the wrapper a real height: it then sits in its own space below the heading.
        <div style={{ padding: '30px 0' }}>
          <LoadablePanel loading>
            <div style={{ minHeight: 120 }} />
          </LoadablePanel>
        </div>
      )}
      <div
        className={css.clsLAReviewPayTimelineContainer}
        style={ready ? undefined : { display: 'none' }}
      >
        <div className={css.clsLAReviewPayTimeline}>
          <div className={css.clsLATimelineItem}>
            <div className={css.clsLATimelineDot} />
            <div className={css.clsLATimelineContent}>
              <div className={css.clsLATimelineHead}>
                <h4 className={css.clsLATimelineLabel}>Due Today</h4>
                <h5 className={css.clsLATimelinePrice}>{currencyFormat(preview.dueToday)}</h5>
              </div>
              <p className={css.clsLATimelineDescription}>
                {connectAdded
                  ? 'Propstream Connect + Investor Outreach plan'
                  : frequencyLabel === 'year'
                    ? 'Investor Outreach'
                    : 'Investor Outreach plan'}{' '}
                begins. {preview.daysUntilNextBillingCycle} days until next billing cycle.
              </p>
            </div>
          </div>

          <div className={css.clsLATimelineLine} />

          <div className={css.clsLATimelineItem}>
            <div className={css.clsLATimelineDot} />
            <div className={css.clsLATimelineContent}>
              <div className={css.clsLATimelineHead}>
                <h4 className={css.clsLATimelineLabel}>
                  Starting {firstBillingDate.format('MMMM D, YYYY')}
                </h4>
                <h5 className={css.clsLATimelinePrice}>
                  {currencyFormat(preview.recurringAmount)}/{frequencyLabel}
                </h5>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className={css.pb_48}>
        <div className={css.clsLAReviewPMCard}>
          <h4 className={css.clsLAReviewPMCardHeading}>Payment Method</h4>
          <CreditCardDropdown
            tag={false}
            options={cardList}
            paymentMethodType={'Subscription'}
            onChange={onCardChange}
          />
        </div>
      </div>

      <div className={css.clsLAReviewContentPara}>
        <p
          className={classNames(css.clsPara, css.mb_16)}
          style={ready ? undefined : { display: 'none' }}
        >
          <span className={css.text_b}>
            Starting on {firstBillingDate.format('MMMM D, YYYY')} you’ll be charged{' '}
            {currencyFormat(preview.recurringAmount, null, { forceDecimal: true })}/{frequencyLabel}{' '}
            automatically until you cancel.
          </span>{' '}
          This does not include any other services you may be subscribed to. Your price may change
          as described in the{' '}
          <a
            target="_blank"
            rel="noreferrer"
            href="https://www.propstream.com/terms-of-use"
            className={css.terms_conditions}
          >
            Terms and Conditions
          </a>
          .
        </p>
        <p className={css.clsPara}>
          By selecting <span style={{ fontWeight: 700 }}>Subscribe Now</span>, you agree to our{' '}
          <a
            target="_blank"
            rel="noreferrer"
            href="https://www.propstream.com/terms-of-use"
            className={css.terms_conditions}
          >
            Terms and Conditions
          </a>
          .
        </p>
      </div>
    </div>
  );
};

// Confirm panel (Figma 1255:125220).
const ReviewConfirmUnsubscribe = ({ preview, ready }) => {
  const endDate = moment(preview.endDate);
  return (
    <div className={css.clsLAReviewPayCard2}>
      <h3 className={css.clsLAReviewHeading}>
        <AccountLogo />
        Review And Confirm Changes
      </h3>
      {!ready && (
        // The hover loader fills its wrapper, so give the wrapper a real height: it then sits in its own space below the heading.
        <div style={{ padding: '30px 0' }}>
          <LoadablePanel loading>
            <div style={{ minHeight: 120 }} />
          </LoadablePanel>
        </div>
      )}
      <div
        className={css.clsLAReviewPayTimelineContainer}
        style={ready ? undefined : { display: 'none' }}
      >
        <div className={css.clsLAReviewPayTimeline}>
          <div className={classNames(css.clsLATimelineItem, css.clsLATimelineItemLined)}>
            <div className={css.clsLATimelineDot} />
            <div className={css.clsLATimelineContent}>
              <div className={css.clsLATimelineHead}>
                <h4 className={css.clsLATimelineLabel}>Today</h4>
              </div>
              <p className={css.clsLATimelineDescription}>
                {preview.daysRemaining} days remaining in your Investor Outreach plan
              </p>
            </div>
          </div>

          <div className={css.clsLATimelineLine2Unsubscribe} />

          <div className={css.clsLATimelineItem}>
            <div className={css.clsLATimelineDot} />
            <div className={css.clsLATimelineContent}>
              <div className={css.clsLATimelineHead}>
                <h4 className={css.clsLATimelineLabel}>{endDate.format('MMMM D, YYYY')}</h4>
              </div>
              <p className={css.clsLATimelineDescription}>
                Investor Outreach will become inactive. Resubscribe any time to restore access.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className={css.clsLAReviewContentPara}>
        <p className={css.clsPara}>
          By selecting <span style={{ fontWeight: 700 }}>Confirm Changes</span>, you agree to our{' '}
          <a
            target="_blank"
            rel="noreferrer"
            href="https://www.propstream.com/terms-of-use"
            className={css.terms_conditions}
          >
            Terms and Conditions
          </a>
          .
        </p>
      </div>
    </div>
  );
};

export default withRouter(ManageInvestorOutreachScreen);
